import { useContext, useEffect, useState } from 'react';
import { getCustomisationValue, updateCustomisationValue } from '../../../EditablePreview/EditablePreview';
import ModuleNumericalInput from '../../components/ModuleNumericalInput';
import './FillModule.scss';
import { changeNumericalFieldValue, changeStringFieldValue, changeValue_noType } from '../../ModuleList';
import ModuleTextInput from '../../components/ModuleTextInput';
import { ShowColorPicker } from '../../../../../../forms/ColorPicker/ColorPickerPopUp';
import { CampaignContext } from "../../../../../../../contexts/CampaignContext";

import ImageIcon from '../../../../WorkspaceAssets/ListIcons/image-icon.png';
import { COLOUR_NAME_TO_COLOUR_HEX } from '../../../../../../../helpers/Colours';
import { evaluateDisplayConditionsForField, renderFirstCustomBlock, renderSecondCustomBlock } from '../../customFields/CustomFieldRenderer';
import ColorPickerField from '../../customFields/colorPicker/ColorPickerField';
import { slideOutPanelControl } from '../../../../../../../containers/TimelineEditor/slideout-panel/SlideoutPanel';
import { WORKING_ON_NODE_DATA } from '../../../EditablePreview/VisualisedComponents/VisualisedNode';

const FillModule = (props) => {
    const moduleConfig = props.moduleConfig;
    const groupConfig = props.groupConfig;
    const customisationVars = props.customisationVars;

    const [campaignContext, setCampaignContext] = useContext(CampaignContext);

    const completelyHideUnneededFields = moduleConfig?.moduleSettings?.hideUnneededFields ?? false;

    const typesFieldConfig = moduleConfig.fields.find(field => field.field.includes('fill-type'));
    const opacityFieldConfig = moduleConfig.fields.find(field => field.field.includes('fill-opacity'));

    const solidFillTypeConfig = typesFieldConfig['customisation-module-fill-type-solid'];
    const gradientFillTypeConfig = typesFieldConfig['customisation-module-fill-type-gradient'];
    const stripedFillTypeConfig = typesFieldConfig['customisation-module-fill-type-stripes'];
    const imageFillTypeConfig = typesFieldConfig['customisation-module-fill-type-image'];
    // Unlinked type is for when the type is not user editable, it will tell us which type is being used
    const unlinkedTypeConfig = typesFieldConfig['customisation-module-fill-type-unlinked-value'];

    const gradientAvailable = (gradientFillTypeConfig?.dataLink && gradientFillTypeConfig?.value) ? true : false;
    const stripedAvailable = (stripedFillTypeConfig?.dataLink && stripedFillTypeConfig?.value) ? true : false;
    const imageAvailable = (imageFillTypeConfig?.dataLink && imageFillTypeConfig?.value) ? true : false;
    const solidAvailable = (solidFillTypeConfig?.dataLink && solidFillTypeConfig?.value) ? true : false;
    const fixedType = (gradientAvailable || stripedAvailable || imageAvailable || solidAvailable) ? false : unlinkedTypeConfig?.enumValue;
    
    let currentType = fixedType !== false ? fixedType.toLowerCase() : 'solid';
    if (fixedType === false) {
        // Discover our type based on the customisationVars
        console.log('--- FillModule: Discovering currentType based on customisationVars', gradientFillTypeConfig, stripedFillTypeConfig, imageFillTypeConfig, solidFillTypeConfig);
        if (gradientAvailable && getCustomisationValue(customisationVars, gradientFillTypeConfig.dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode).toString() === gradientFillTypeConfig.value.toLowerCase()) {
            currentType = 'gradient';
        }
        else if (stripedAvailable && getCustomisationValue(customisationVars, stripedFillTypeConfig.dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode).toString() === stripedFillTypeConfig.value.toLowerCase()) {
            currentType = 'stripes';
        }
        else if (imageAvailable && getCustomisationValue(customisationVars, imageFillTypeConfig.dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode).toString() === imageFillTypeConfig.value.toLowerCase()) {
            currentType = 'image';
        }
        else if (solidAvailable && getCustomisationValue(customisationVars, solidFillTypeConfig.dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode).toString() === solidFillTypeConfig.value.toLowerCase()) {
            currentType = 'solid';
        }
    }

    const showTypeSelector = (solidAvailable && evaluateDisplayConditionsForField(props, solidFillTypeConfig)) || (gradientAvailable && evaluateDisplayConditionsForField(props, gradientFillTypeConfig)) || (stripedAvailable && evaluateDisplayConditionsForField(props, stripedFillTypeConfig)) || (imageAvailable && evaluateDisplayConditionsForField(props, imageFillTypeConfig));
    const showOpacityField = opacityFieldConfig?.dataLink && evaluateDisplayConditionsForField(props, opacityFieldConfig);

    const [opacity, setOpacity] = useState(getCustomisationValue(customisationVars, opacityFieldConfig?.dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode) ?? '100');
    const changeOpacityFieldValue = (value) => {
        if (opacityFieldConfig) {
            if (changeNumericalFieldValue(customisationVars, opacityFieldConfig, value, true, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode)) {
                setOpacity(value);
                props?.forceUpdateComponent?.();
            }
        }
    }
    const changeFillType = (type) => {
        if (fixedType === false) {
            if (type === 'solid' && solidAvailable) {
                updateCustomisationValue(customisationVars, solidFillTypeConfig.dataLink, solidFillTypeConfig.value, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode);
            } else if (type === 'gradient' && gradientAvailable) {
                updateCustomisationValue(customisationVars, gradientFillTypeConfig.dataLink, gradientFillTypeConfig.value, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode);
            } else if (type === 'stripes' && stripedAvailable) {
                updateCustomisationValue(customisationVars, stripedFillTypeConfig.dataLink, stripedFillTypeConfig.value, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode);
            } else if (type === 'image' && imageAvailable) {
                updateCustomisationValue(customisationVars, imageFillTypeConfig.dataLink, imageFillTypeConfig.value, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode);
            }
            props?.forceUpdateComponent?.();
        }
    };

    /* const [solidFillColor, setSolidFillColor] = useState(getCustomisationValue(customisationVars, solidFillTypeConfig?.options?.['customisation-module-fill-type-solid-options-color']?.dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode) ?? '#000000');
    const changeSolidFillColor = (value) => {
        if (solidFillTypeConfig?.options?.['customisation-module-fill-type-solid-options-color']?.dataLink) {
            if (changeStringFieldValue(customisationVars, solidFillTypeConfig.options['customisation-module-fill-type-solid-options-color'], value, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode)) {
                setSolidFillColor(value);
                props?.forceUpdateComponent?.();
            }
        }
    } */

    /* const [gradientColor1, setGradientColor1] = useState(getCustomisationValue(customisationVars, gradientFillTypeConfig?.options?.['customisation-module-fill-type-gradient-options-color1']?.dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode) ?? '#000000');
    const [gradientColor2, setGradientColor2] = useState(getCustomisationValue(customisationVars, gradientFillTypeConfig?.options?.['customisation-module-fill-type-gradient-options-color2']?.dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode) ?? '#FFFFFF'); */
    const [gradientAngle, setGradientAngle] = useState(getCustomisationValue(customisationVars, gradientFillTypeConfig?.options?.['customisation-module-fill-type-gradient-options-angle']?.dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode) ?? '0');
    /* const changeGradientColor1 = (value) => {
        if (gradientFillTypeConfig?.options?.['customisation-module-fill-type-gradient-options-color1']?.dataLink) {
            if (changeStringFieldValue(customisationVars, gradientFillTypeConfig.options['customisation-module-fill-type-gradient-options-color1'], value, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode)) {
                setGradientColor1(value);
                props?.forceUpdateComponent?.();
            }
        }
    }
    const changeGradientColor2 = (value) => {
        if (gradientFillTypeConfig?.options?.['customisation-module-fill-type-gradient-options-color2']?.dataLink) {
            if (changeStringFieldValue(customisationVars, gradientFillTypeConfig.options['customisation-module-fill-type-gradient-options-color2'], value, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode)) {
                setGradientColor2(value);
                props?.forceUpdateComponent?.();
            }
        }
    } */
    const changeGradientAngle = (value) => {
        if (gradientFillTypeConfig?.options?.['customisation-module-fill-type-gradient-options-angle']?.dataLink) {
            if (changeNumericalFieldValue(customisationVars, gradientFillTypeConfig.options['customisation-module-fill-type-gradient-options-angle'], value, true, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode)) {
                setGradientAngle(value);
                props?.forceUpdateComponent?.();
            }
        }
    }
    const showGradientAngle = gradientFillTypeConfig?.options?.['customisation-module-fill-type-gradient-options-angle']?.dataLink ? true : false;

    /* const [stripedColor1, setStripedColor1] = useState(getCustomisationValue(customisationVars, stripedFillTypeConfig?.options?.['customisation-module-fill-type-stripes-options-color1']?.dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode) ?? '#000000');
    const [stripedColor2, setStripedColor2] = useState(getCustomisationValue(customisationVars, stripedFillTypeConfig?.options?.['customisation-module-fill-type-stripes-options-color2']?.dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode) ?? '#FFFFFF'); */
    const [stripedAngle, setStripedAngle] = useState(getCustomisationValue(customisationVars, stripedFillTypeConfig?.options?.['customisation-module-fill-type-stripes-options-angle']?.dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode) ?? '-');
    const [stripedDiagonal, setStripedDiagonal] = useState(getCustomisationValue(customisationVars, stripedFillTypeConfig?.options?.['customisation-module-fill-type-stripes-options-diagonal']?.dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode) ?? false);
    const [stripedStraight, setStripedStraight] = useState(getCustomisationValue(customisationVars, stripedFillTypeConfig?.options?.['customisation-module-fill-type-stripes-options-straight']?.dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode) ?? false);
    const [stripedWidth, setStripedWidth] = useState(getCustomisationValue(customisationVars, stripedFillTypeConfig?.options?.['customisation-module-fill-type-stripes-options-width']?.dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode) ?? '-');
    
    const stripedDiagonalAvailable = stripedFillTypeConfig?.options?.['customisation-module-fill-type-stripes-options-diagonal']?.dataLink ? true : false;
    const stripedStraightAvailable = stripedFillTypeConfig?.options?.['customisation-module-fill-type-stripes-options-straight']?.dataLink ? true : false;
    
    /* const changeStripedColor1 = (value) => {
        if (stripedFillTypeConfig?.options?.['customisation-module-fill-type-stripes-options-color1']?.dataLink) {
            if (changeStringFieldValue(customisationVars, stripedFillTypeConfig.options['customisation-module-fill-type-stripes-options-color1'], value, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode)) {
                setStripedColor1(value);
                props?.forceUpdateComponent?.();
            }
        }
    }
    const changeStripedColor2 = (value) => {
        if (stripedFillTypeConfig?.options?.['customisation-module-fill-type-stripes-options-color2']?.dataLink) {
            if (changeStringFieldValue(customisationVars, stripedFillTypeConfig.options['customisation-module-fill-type-stripes-options-color2'], value, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode)) {
                setStripedColor2(value);
                props?.forceUpdateComponent?.();
            }
        }
    } */
    const changeStripedAngle = (value) => {
        if (stripedFillTypeConfig?.options?.['customisation-module-fill-type-stripes-options-angle']?.dataLink) {
            if (changeNumericalFieldValue(customisationVars, stripedFillTypeConfig.options['customisation-module-fill-type-stripes-options-angle'], value, true, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode)) {
                setStripedAngle(value);
                props?.forceUpdateComponent?.();
            }
        }
    }
    const changeStripedDiagonal = () => {
        if (stripedFillTypeConfig?.options?.['customisation-module-fill-type-stripes-options-diagonal']?.dataLink && stripedFillTypeConfig?.options?.['customisation-module-fill-type-stripes-options-diagonal'].value !== undefined) {
            const value = stripedFillTypeConfig.options['customisation-module-fill-type-stripes-options-diagonal'].value;
            if (changeStringFieldValue(customisationVars, stripedFillTypeConfig.options['customisation-module-fill-type-stripes-options-diagonal'], value, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode)) {
                setStripedDiagonal(value);
                props?.forceUpdateComponent?.();
            }
        }
    }
    const changeStripedStraight = () => {
        if (stripedFillTypeConfig?.options?.['customisation-module-fill-type-stripes-options-straight']?.dataLink && stripedFillTypeConfig?.options?.['customisation-module-fill-type-stripes-options-straight'].value !== undefined) {
            const value = stripedFillTypeConfig.options['customisation-module-fill-type-stripes-options-straight'].value;
            if (changeStringFieldValue(customisationVars, stripedFillTypeConfig.options['customisation-module-fill-type-stripes-options-straight'], value, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode)) {
                setStripedStraight(value);
                props?.forceUpdateComponent?.();
            }
        }
    }
    const changeStripedWidth = (value) => {
        if (stripedFillTypeConfig?.options?.['customisation-module-fill-type-stripes-options-width']?.dataLink) {
            if (changeNumericalFieldValue(customisationVars, stripedFillTypeConfig.options['customisation-module-fill-type-stripes-options-width'], value, true, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode)) {
                setStripedWidth(value);
                props?.forceUpdateComponent?.();
            }
        }
    }

    const [imageFileData, setImageFileData] = useState(getCustomisationValue(customisationVars, imageFillTypeConfig?.options?.['customisation-module-fill-type-image-options-image']?.dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode) ?? null);
    
    const tileImageDataLinkObj = imageFillTypeConfig?.options?.['customisation-module-fill-type-image-options-fill-style']?.['customisation-module-fill-type-image-options-tile']?.["customisation-module-fill-type-image-options-tile-data-link"];
    const coverImageDataLinkObj = imageFillTypeConfig?.options?.['customisation-module-fill-type-image-options-fill-style']?.['customisation-module-fill-type-image-options-cover']?.["customisation-module-fill-type-image-options-cover-data-link"];
    const containImageDataLinkObj = imageFillTypeConfig?.options?.['customisation-module-fill-type-image-options-fill-style']?.['customisation-module-fill-type-image-options-contain']?.["customisation-module-fill-type-image-options-contain-data-link"];

    const tileImageDataLink = imageFillTypeConfig?.options?.['customisation-module-fill-type-image-options-fill-style']?.['customisation-module-fill-type-image-options-tile']?.["customisation-module-fill-type-image-options-tile-data-link"]?.dataLink;
    const tileImageValueLink = imageFillTypeConfig?.options?.['customisation-module-fill-type-image-options-fill-style']?.['customisation-module-fill-type-image-options-tile']?.["customisation-module-fill-type-image-options-tile-value"]?.value;
    const coverImageDataLink = imageFillTypeConfig?.options?.['customisation-module-fill-type-image-options-fill-style']?.['customisation-module-fill-type-image-options-cover']?.["customisation-module-fill-type-image-options-cover-data-link"]?.dataLink;
    const coverImageValueLink = imageFillTypeConfig?.options?.['customisation-module-fill-type-image-options-fill-style']?.['customisation-module-fill-type-image-options-cover']?.["customisation-module-fill-type-image-options-cover-value"]?.value;
    const containImageDataLink = imageFillTypeConfig?.options?.['customisation-module-fill-type-image-options-fill-style']?.['customisation-module-fill-type-image-options-contain']?.["customisation-module-fill-type-image-options-contain-data-link"]?.dataLink;
    const containImageValueLink = imageFillTypeConfig?.options?.['customisation-module-fill-type-image-options-fill-style']?.['customisation-module-fill-type-image-options-contain']?.["customisation-module-fill-type-image-options-contain-value"]?.value;

    const [tileImage, setTileImage] = useState(getCustomisationValue(customisationVars, tileImageDataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode) ?? false);
    const [coverImage, setCoverImage] = useState(getCustomisationValue(customisationVars, coverImageDataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode) ?? false);
    const [containImage, setContainImage] = useState(getCustomisationValue(customisationVars, containImageDataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode) ?? false);
    useEffect(() => {
        setTileImage(getCustomisationValue(customisationVars, tileImageDataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode) ?? false);
        setCoverImage(getCustomisationValue(customisationVars, coverImageDataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode) ?? false);
        setContainImage(getCustomisationValue(customisationVars, containImageDataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode) ?? false);
    }, [customisationVars, tileImageDataLink, coverImageDataLink, containImageDataLink, props.index, props.visualisationComponentKey, props.selectedFeatureNode, tileImage, coverImage, containImage]);

    const tileImageOptionAvailable = tileImageDataLink ? true : false;
    const coverImageOptionAvailable = coverImageDataLink ? true : false;
    const containImageOptionAvailable = containImageDataLink ? true : false;

    const changeImageFillTypeTile = () => {
        if (tileImageOptionAvailable && tileImageDataLink) {
            const value = tileImageValueLink;
            if (value.toLowerCase() === 'true' || value.toLowerCase() === 'false') {
                if (changeValue_noType(customisationVars, tileImageDataLinkObj, value.toLowerCase() === 'true', props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode)) {
                    setTileImage(value.toLowerCase() === 'true');
                    setCoverImage(value.toLowerCase() === 'true');
                    setContainImage(value.toLowerCase() === 'true');
                    props?.forceUpdateComponent?.();
                }
            } else
            if (changeStringFieldValue(customisationVars, tileImageDataLinkObj, value, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode)) {
                setTileImage(value);
                props?.forceUpdateComponent?.();
            }
        }
    }
    const changeImageFillTypeCover = () => {
        if (coverImageOptionAvailable && coverImageDataLink) {
            const value = coverImageValueLink;
            if (value.toLowerCase() === 'true' || value.toLowerCase() === 'false') {
                if (changeValue_noType(customisationVars, coverImageDataLinkObj, value.toLowerCase() === 'true', props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode)) {
                    setCoverImage(value.toLowerCase() === 'true');
                    setTileImage(value.toLowerCase() === 'true');
                    setContainImage(value.toLowerCase() === 'true');
                    props?.forceUpdateComponent?.();
                }
            } else
            if (changeStringFieldValue(customisationVars, coverImageDataLinkObj, value, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode)) {
                setCoverImage(value);
                props?.forceUpdateComponent?.();
            }
        }
    }
    const changeImageFillTypeContain = () => {
        // console.log('--- FillModule: changeImageFillTypeContain called', containImageOptionAvailable, containImageDataLink);
        if (containImageOptionAvailable && containImageDataLink) {
            const value = containImageValueLink;
            // console.log('--- FillModule: changeImageFillTypeContain value:', value, containImageDataLinkObj);
            if (value.toLowerCase() === 'true' || value.toLowerCase() === 'false') {
                if (changeValue_noType(customisationVars, containImageDataLinkObj, value.toLowerCase() === 'true', props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode)) {
                    setContainImage(value.toLowerCase() === 'true');
                    setTileImage(value.toLowerCase() === 'true');
                    setCoverImage(value.toLowerCase() === 'true');
                    props?.forceUpdateComponent?.();
                }
            } else
            if (changeStringFieldValue(customisationVars, containImageDataLinkObj, value, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode)) {
                // console.log('--- FillModule: changeImageFillTypeContain successful changeStringFieldValue');
                setContainImage(value);
                props?.forceUpdateComponent?.();
            }
        }
    }

    useEffect(() => {
        if (opacityFieldConfig?.dataLink) {
            const opacityValue = getCustomisationValue(customisationVars, opacityFieldConfig.dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode);
            setOpacity(opacityValue ?? '100');
        } else {
            setOpacity('100');
        }
        /* if (solidFillTypeConfig?.options?.['customisation-module-fill-type-solid-options-color']?.dataLink) {
            const solidFillColorValue = getCustomisationValue(customisationVars, solidFillTypeConfig.options['customisation-module-fill-type-solid-options-color'].dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode);
            setSolidFillColor(solidFillColorValue ?? '#000000');
        } else {
            setSolidFillColor('#000000');
        } */

        /* if (gradientFillTypeConfig?.options?.['customisation-module-fill-type-gradient-options-color1']?.dataLink) {
            const gradientColor1Value = getCustomisationValue(customisationVars, gradientFillTypeConfig.options['customisation-module-fill-type-gradient-options-color1'].dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode);
            setGradientColor1(gradientColor1Value ?? '#000000');
        } else {
            setGradientColor1('#000000');
        }
        if (gradientFillTypeConfig?.options?.['customisation-module-fill-type-gradient-options-color2']?.dataLink) {
            const gradientColor2Value = getCustomisationValue(customisationVars, gradientFillTypeConfig.options['customisation-module-fill-type-gradient-options-color2'].dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode);
            setGradientColor2(gradientColor2Value ?? '#FFFFFF');
        } else {
            setGradientColor2('#FFFFFF');
        } */
        if (gradientFillTypeConfig?.options?.['customisation-module-fill-type-gradient-options-angle']?.dataLink) {
            const gradientAngleValue = getCustomisationValue(customisationVars, gradientFillTypeConfig.options['customisation-module-fill-type-gradient-options-angle'].dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode);
            setGradientAngle(gradientAngleValue ?? '0');
        } else {
            setGradientAngle('0');
        }
        /* if (stripedFillTypeConfig?.options?.['customisation-module-fill-type-stripes-options-color1']?.dataLink) {
            const stripedColor1Value = getCustomisationValue(customisationVars, stripedFillTypeConfig.options['customisation-module-fill-type-stripes-options-color1'].dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode);
            setStripedColor1(stripedColor1Value ?? '#000000');
        } else {
            setStripedColor1('#000000');
        }
        if (stripedFillTypeConfig?.options?.['customisation-module-fill-type-stripes-options-color2']?.dataLink) {
            const stripedColor2Value = getCustomisationValue(customisationVars, stripedFillTypeConfig.options['customisation-module-fill-type-stripes-options-color2'].dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode);
            setStripedColor2(stripedColor2Value ?? '#FFFFFF');
        } else {
            setStripedColor2('#FFFFFF');
        } */
        if (stripedFillTypeConfig?.options?.['customisation-module-fill-type-stripes-options-angle']?.dataLink) {
            const stripedAngleValue = getCustomisationValue(customisationVars, stripedFillTypeConfig.options['customisation-module-fill-type-stripes-options-angle'].dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode);
            setStripedAngle(stripedAngleValue ?? '-');
        } else {
            setStripedAngle('-');
        }
        if (stripedFillTypeConfig?.options?.['customisation-module-fill-type-stripes-options-diagonal']?.dataLink) {
            const stripedDiagonalValue = getCustomisationValue(customisationVars, stripedFillTypeConfig.options['customisation-module-fill-type-stripes-options-diagonal'].dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode);
            setStripedDiagonal(stripedDiagonalValue ?? false);
        } else {
            setStripedDiagonal(false);
        }
        if (stripedFillTypeConfig?.options?.['customisation-module-fill-type-stripes-options-straight']?.dataLink) {
            const stripedStraightValue = getCustomisationValue(customisationVars, stripedFillTypeConfig.options['customisation-module-fill-type-stripes-options-straight'].dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode);
            setStripedStraight(stripedStraightValue ?? false);
        } else {
            setStripedStraight(false);
        }
        if (stripedFillTypeConfig?.options?.['customisation-module-fill-type-stripes-options-width']?.dataLink) {
            const stripedWidthValue = getCustomisationValue(customisationVars, stripedFillTypeConfig.options['customisation-module-fill-type-stripes-options-width'].dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode);
            setStripedWidth(stripedWidthValue ?? '-');
        } else {
            setStripedWidth('-');
        }
        if (imageFillTypeConfig?.options?.['customisation-module-fill-type-image-options-image']?.dataLink) {
            const imageFillDataValue = getCustomisationValue(customisationVars, imageFillTypeConfig.options['customisation-module-fill-type-image-options-image'].dataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode);
            setImageFileData(imageFillDataValue ?? null);
        } else {
            setImageFileData(null);
        }
        if (tileImageDataLink) {
            const tileImageValue = getCustomisationValue(customisationVars, tileImageDataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode);
            setTileImage(tileImageValue ?? false);
        } else {
            setTileImage(false);
        }
        if (coverImageDataLink) {
            const coverImageValue = getCustomisationValue(customisationVars, coverImageDataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode);
            setCoverImage(coverImageValue ?? false);
        } else {
            setCoverImage(false);
        }
        if (containImageDataLink) {
            const containImageValue = getCustomisationValue(customisationVars, containImageDataLink, props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode);
            setContainImage(containImageValue ?? false);
        } else {
            setContainImage(false);
        }

    }, [moduleConfig, customisationVars, opacityFieldConfig/* , solidFillTypeConfig *//* , props.forceUpdate */, gradientFillTypeConfig, stripedFillTypeConfig/* , props.forceUpdateComponent */, imageFillTypeConfig]);


    // console.log('FillModule rendered with currentType:', currentType, 'opacity:', opacity, 'solidFillColor:', solidFillColor, 'gradientColor1:', gradientColor1, 'gradientColor2:', gradientColor2, 'gradientAngle:', gradientAngle, 'stripedColor1:', stripedColor1, 'stripedColor2:', stripedColor2, 'stripedAngle:', stripedAngle, 'stripedDiagonal:', stripedDiagonal, 'stripedStraight:', stripedStraight, 'stripedWidth:', stripedWidth, 'imageFillData:', imageFillData, 'tileImage:', tileImage, 'coverImage:', coverImage, 'containImage:', containImage);
    const changeImageValue = (imageData, fieldData, isFinalSelection = true) => {
        console.log('ImageField: Changing image value:', imageData, 'isFinalSelection:', isFinalSelection);
        console.log('ImageField: imageData type:', typeof imageData, 'full object:', JSON.stringify(imageData, null, 2));
        console.log('ImageField: fieldData:', fieldData, 'dataLink:', fieldData?.dataLink);
        // Set working on node data for proper context
        WORKING_ON_NODE_DATA.fieldData = fieldData;
        WORKING_ON_NODE_DATA.node = props.selectedFeatureNode;
        
        // Extract the filename and build the proper object structure for database storage
        // Database expects: {filename: "name.png", isDefault: false, isStreamLevel: false}
        let valueToStore = null;
        if (imageData && imageData.filename) {
            let filename = null;
            
            // Extract filename from various possible formats
            if (typeof imageData === 'object' && imageData.filename) {
                filename = imageData.filename;
            } else if (typeof imageData === 'object' && imageData.originalFile) {
                filename = imageData.originalFile;
            } else if (typeof imageData === 'string') {
                filename = imageData;
            }
            
            // Only store if we have a non-empty filename
            if (filename && filename.trim() !== '') {
                valueToStore = {
                    filename: filename,
                    isDefault: false,
                    isStreamLevel: false
                };
            } else {
                // Empty filename - clear the field
                valueToStore = {
                    filename: '',
                    isDefault: false,
                    isStreamLevel: false
                };
            }
        } else {
            // imageData is null/undefined or has no filename, so clear just the filename
            // Don't pass null - pass an empty object so the field data structure remains
            valueToStore = {
                filename: '',
                isDefault: false,
                isStreamLevel: false
            };
        }
        
        console.log('ImageField: Storing value:', valueToStore);
        console.log('ImageField: Updating field with dataLink:', fieldData?.customFieldConfig?.dataLink, 'index:', props.index, 'visualisationComponentKey:', props.visualisationComponentKey);
        
        // Update using the proper changeStringFieldValue helper
        let valChanged = changeValue_noType(
            customisationVars,
            fieldData,
            imageData, // valueToStore,
            props.index ?? 0,
            props.visualisationComponentKey ?? null,
            props.selectedFeatureNode,
            isFinalSelection  // Pass isFinalValue parameter
        );
        
        console.log('ImageField: Field change result:', valChanged);
        
        // Update local state with the full image data (for display purposes)
        if (valChanged) {
            setImageFileData(imageData);
        }
    };

    return (
        <div className="v2-basic-module-holder">
            <h5>{moduleConfig.name}</h5>

            {renderFirstCustomBlock(props)}

            {(showTypeSelector || showOpacityField || !completelyHideUnneededFields) &&
                <div className='v2-module-columns-holder'>
                    {(showTypeSelector || !completelyHideUnneededFields) &&
                        <div className='v2-module-one-column'>
                            <div className='v2-module-field-label'>Type</div>
                            <div className='v2-module-square-button-group'>
                                <div className='v2-module-square-button-holder'>
                                    <div className={`v2-module-square-button${currentType === 'solid' ? ' v2-module-button__active v2-no-mouse-events' : ''}${currentType !== 'solid' && (!solidAvailable || !showTypeSelector) ? ' v2-module-disabled-field' : ''}`}
                                        onClick={() => changeFillType('solid')}
                                    >
                                        <div className={`v2-module-square-button__icon v2-module-filltype-solid`} />
                                    </div>
                                </div>
                                <div className='v2-module-square-button-holder'>
                                    <div className={`v2-module-square-button${currentType === 'image' ? ' v2-module-button__active v2-no-mouse-events' : ''}${currentType !== 'image' && (!imageAvailable || !showTypeSelector) ? ' v2-module-disabled-field' : ''}`}
                                        onClick={() => changeFillType('image')}
                                    >
                                        <div className={`v2-module-square-button__icon v2-module-filltype-image`} />
                                    </div>
                                </div>
                                <div className='v2-module-square-button-holder'>
                                    <div className={`v2-module-square-button${currentType === 'gradient' ? ' v2-module-button__active v2-no-mouse-events' : ''}${currentType !== 'gradient' && (!gradientAvailable || !showTypeSelector) ? ' v2-module-disabled-field' : ''}`}
                                        onClick={() => changeFillType('gradient')}
                                    >
                                        <div className={`v2-module-square-button__icon v2-module-filltype-gradient`} />
                                    </div>
                                </div>
                                <div className='v2-module-square-button-holder'>
                                    <div className={`v2-module-square-button${currentType === 'stripes' ? ' v2-module-button__active v2-no-mouse-events' : ''}${currentType !== 'stripes' && (!stripedAvailable || !showTypeSelector) ? ' v2-module-disabled-field' : ''}`}
                                        onClick={() => changeFillType('stripes')}
                                    >
                                        <div className={`v2-module-square-button__icon v2-module-filltype-striped`} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    }
                    {(showOpacityField || !completelyHideUnneededFields) &&
                        <div className='v2-module-one-column'>
                            <div className={`v2-module-field-label${!opacityFieldConfig?.dataLink ? ' disabled' : ''}`}>Opacity</div>
                            <ModuleNumericalInput
                                fieldLabel={<div className="v2-module-block-field-input-label" style={{opacity: opacity / 100}} />}
                                value={opacity}
                                fieldId={'opacity'}
                                enabled={!!opacityFieldConfig?.dataLink}
                                onChange={(fieldId, value) => changeOpacityFieldValue(value)}
                                hiddenDropDown={true}
                                hiddenDropDownOptions={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
                            />
                        </div>
                    }
                    { currentType === 'solid' && (showTypeSelector || !completelyHideUnneededFields) && (!showOpacityField && completelyHideUnneededFields) &&
                            <div className='v2-module-one-column'>
                                {/* <div className='v2-module-field-label'>Color</div>
                                <ModuleTextInput
                                    fieldLabel={<div className="v2-module-block-field-input-label" style={{backgroundColor: COLOUR_NAME_TO_COLOUR_HEX(solidFillColor, campaignContext.campaignData)}} />}
                                    value={solidFillColor}
                                    fieldId={'fill-color'}
                                    enabled={true}
                                    editable={false}
                                    onChange={null}
                                    onClick={() => {ShowColorPicker(solidFillColor, changeSolidFillColor, campaignContext.campaignData, 'Solid Fill Color', () => {}, () => {})}}
                                    maxLength={7}
                                /> */}
                                <ColorPickerField
                                    fieldData={solidFillTypeConfig?.options?.['customisation-module-fill-type-solid-options-color']}
                                    customisationVars={customisationVars}
                                    index={props.index}
                                    visualisationComponentKey={props.visualisationComponentKey}
                                    selectedFeatureNode={props.selectedFeatureNode}
                                    colourPickerTitle={'Solid Fill Color'}
                                    fieldTitle={'Color'}
                                    forceUpdateComponent={props.forceUpdateComponent}
                                />
                            </div>
                    }
                </div>
            }
            { currentType === 'solid' && (showTypeSelector || !completelyHideUnneededFields) && (showOpacityField || !completelyHideUnneededFields) &&
                <div className='v2-module-columns-holder'>
                    <div className='v2-module-one-column'>
                        {/* <div className='v2-module-field-label'>Color</div>
                        <ModuleTextInput
                            fieldLabel={<div className="v2-module-block-field-input-label" style={{backgroundColor: COLOUR_NAME_TO_COLOUR_HEX(solidFillColor, campaignContext.campaignData)}} />}
                            value={solidFillColor}
                            fieldId={'fill-color'}
                            enabled={true}
                            editable={false}
                            onChange={null}
                            onClick={() => {ShowColorPicker(solidFillColor, changeSolidFillColor, campaignContext.campaignData, 'Solid Fill Color', () => {}, () => {})}}
                            maxLength={7}
                        /> */}
                        <ColorPickerField
                            fieldData={solidFillTypeConfig?.options?.['customisation-module-fill-type-solid-options-color']}
                            customisationVars={customisationVars}
                            index={props.index}
                            visualisationComponentKey={props.visualisationComponentKey}
                            selectedFeatureNode={props.selectedFeatureNode}
                            colourPickerTitle={'Solid Fill Color'}
                            fieldTitle={'Color'}
                            forceUpdateComponent={props.forceUpdateComponent}
                        />
                    </div>
                    <div className='v2-module-one-column' />
                </div>
            }
            { currentType === 'gradient' && (showTypeSelector || !completelyHideUnneededFields) &&
                <>
                    <div className='v2-module-columns-holder'>
                        <div className='v2-module-one-column'>
                            {/* <div className='v2-module-field-label'>Color 1</div>
                            <ModuleTextInput
                                fieldLabel={<div className="v2-module-block-field-input-label" style={{backgroundColor: COLOUR_NAME_TO_COLOUR_HEX(gradientColor1, campaignContext.campaignData)}} />}
                                value={gradientColor1}
                                fieldId={'gradient-color-1'}
                                enabled={true}
                                editable={false}
                                onChange={null}
                                onClick={() => {ShowColorPicker(gradientColor1, changeGradientColor1, campaignContext.campaignData, 'Gradient Color 1', () => {}, () => {})}}
                                maxLength={7}
                            /> */}
                            <ColorPickerField
                                fieldData={gradientFillTypeConfig?.options?.['customisation-module-fill-type-gradient-options-color1']}
                                customisationVars={customisationVars}
                                index={props.index}
                                visualisationComponentKey={props.visualisationComponentKey}
                                selectedFeatureNode={props.selectedFeatureNode}
                                colourPickerTitle={'Gradient Color 1'}
                                fieldTitle={'Color 1'}
                                forceUpdateComponent={props.forceUpdateComponent}
                            />
                        </div>
                        <div className='v2-module-one-column'>
                            {/* <div className='v2-module-field-label'>Color 2</div>
                            <ModuleTextInput
                                fieldLabel={<div className="v2-module-block-field-input-label" style={{backgroundColor: COLOUR_NAME_TO_COLOUR_HEX(gradientColor2, campaignContext.campaignData)}} />}
                                value={gradientColor2}
                                fieldId={'gradient-color-2'}
                                enabled={true}
                                editable={false}
                                onChange={null}
                                onClick={() => {ShowColorPicker(gradientColor2, changeGradientColor2, campaignContext.campaignData, 'Gradient Color 2', () => {}, () => {})}}
                                maxLength={7}
                            /> */}
                            <ColorPickerField
                                fieldData={gradientFillTypeConfig?.options?.['customisation-module-fill-type-gradient-options-color2']}
                                customisationVars={customisationVars}
                                index={props.index}
                                visualisationComponentKey={props.visualisationComponentKey}
                                selectedFeatureNode={props.selectedFeatureNode}
                                colourPickerTitle={'Gradient Color 2'}
                                fieldTitle={'Color 2'}
                                forceUpdateComponent={props.forceUpdateComponent}
                            />
                        </div>
                    </div>
                    {(showGradientAngle || !completelyHideUnneededFields) &&
                        <div className='v2-module-columns-holder'>
                            <div className='v2-module-one-column'>
                                <div className={`v2-module-field-label${!gradientFillTypeConfig?.options?.['customisation-module-fill-type-gradient-options-angle']?.dataLink ? ' disabled' : ''}`}>Angle</div>
                                <ModuleNumericalInput
                                    // fieldLabel={<div className="v2-module-block-field-input-label" style={{opacity: gradientAngle / 100}} />}
                                    value={gradientAngle}
                                    fieldId={'gradient-angle'}
                                    enabled={!!gradientFillTypeConfig?.options?.['customisation-module-fill-type-gradient-options-angle']?.dataLink}
                                    onChange={(fieldId, value) => changeGradientAngle(value)}
                                    hiddenDropDown={true}
                                    hiddenDropDownOptions={['0', '45', '90', '135', '180', '225', '270', '315']}
                                />
                            </div>
                            <div className='v2-module-one-column' />
                        </div>
                    }
                </>
            }
            {currentType === 'stripes' && (showTypeSelector || !completelyHideUnneededFields) &&
                <>
                    <div className='v2-module-columns-holder'>
                        <div className='v2-module-one-column'>
                            {/* <div className='v2-module-field-label'>Color 1</div>
                            <ModuleTextInput
                                fieldLabel={<div className="v2-module-block-field-input-label" style={{backgroundColor: COLOUR_NAME_TO_COLOUR_HEX(stripedColor1, campaignContext.campaignData)}} />}
                                value={stripedColor1}
                                fieldId={'striped-color-1'}
                                enabled={true}
                                editable={false}
                                onChange={null}
                                onClick={() => {ShowColorPicker(stripedColor1, changeStripedColor1, campaignContext.campaignData, 'Striped Color 1', () => {}, () => {})}}
                                maxLength={7}
                            /> */}
                            <ColorPickerField
                                fieldData={stripedFillTypeConfig?.options?.['customisation-module-fill-type-stripes-options-color1']}
                                customisationVars={customisationVars}
                                index={props.index}
                                visualisationComponentKey={props.visualisationComponentKey}
                                selectedFeatureNode={props.selectedFeatureNode}
                                colourPickerTitle={'Striped Color 1'}
                                fieldTitle={'Color 1'}
                                forceUpdateComponent={props.forceUpdateComponent}
                            />
                        </div>
                        <div className='v2-module-one-column'>
                            {/* <div className='v2-module-field-label'>Color 2</div>
                            <ModuleTextInput
                                fieldLabel={<div className="v2-module-block-field-input-label" style={{backgroundColor: COLOUR_NAME_TO_COLOUR_HEX(stripedColor2, campaignContext.campaignData)}} />}
                                value={stripedColor2}
                                fieldId={'striped-color-2'}
                                enabled={true}
                                editable={false}
                                onChange={null}
                                onClick={() => {ShowColorPicker(stripedColor2, changeStripedColor2, campaignContext.campaignData, 'Striped Color 2', () => {}, () => {})}}
                                maxLength={7}
                            /> */}
                            <ColorPickerField
                                fieldData={stripedFillTypeConfig?.options?.['customisation-module-fill-type-stripes-options-color2']}
                                customisationVars={customisationVars}
                                index={props.index}
                                visualisationComponentKey={props.visualisationComponentKey}
                                selectedFeatureNode={props.selectedFeatureNode}
                                colourPickerTitle={'Striped Color 2'}
                                fieldTitle={'Color 2'}
                                forceUpdateComponent={props.forceUpdateComponent}
                            />
                        </div>
                    </div>
                    <div className='v2-module-columns-holder'>
                        <div className='v2-module-one-column'>
                            <div className='v2-module-field-label'>Direction</div>
                            <div className='v2-module-square-button-group'>
                                <div className='v2-module-square-button-holder'>
                                    <div className={`v2-module-square-button${(!stripedDiagonalAvailable || stripedDiagonal === stripedFillTypeConfig?.options?.['customisation-module-fill-type-stripes-options-diagonal']?.value) ? ' v2-module-button__active v2-no-mouse-events' : ''}`}
                                        onClick={changeStripedDiagonal}
                                    >
                                        <div className={`v2-module-square-button__icon v2-module-filltype-striped-diagonal`} />
                                    </div>
                                </div>
                                <div className='v2-module-square-button-holder'>
                                    <div className={`v2-module-square-button${(stripedStraightAvailable && stripedStraight === stripedFillTypeConfig?.options?.['customisation-module-fill-type-stripes-options-straight']?.value) ? ' v2-module-button__active v2-no-mouse-events' : ''}${!stripedStraightAvailable ? ' v2-module-disabled-field' : ''}`}
                                        onClick={changeStripedStraight}
                                    >
                                        <div className={`v2-module-square-button__icon v2-module-filltype-striped-straight`} />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className='v2-module-one-column'>
                            <div className={`v2-module-field-label${!stripedFillTypeConfig?.options?.['customisation-module-fill-type-stripes-options-width']?.dataLink ? ' disabled' : ''}`}>Width</div>
                            <ModuleNumericalInput
                                fieldLabel={<div className="v2-module-block-field-input-label" style={{height: stripedWidth}} />}
                                value={stripedWidth}
                                fieldId={'striped-width'}
                                enabled={!!stripedFillTypeConfig?.options?.['customisation-module-fill-type-stripes-options-width']?.dataLink}
                                onChange={(fieldId, value) => changeStripedWidth(value)}
                            />
                        </div>
                    </div>
                </>
            }
            {currentType === 'image' && (showTypeSelector || !completelyHideUnneededFields) &&
                <>
                    <div className='v2-module-columns-holder'>
                        <div className='v2-module-one-column'>
                            <div className={`v2-module-field-label${(!tileImageOptionAvailable && !coverImageOptionAvailable && !containImageOptionAvailable) ? ' disabled' : ''}`}>Fill Style</div>
                            <div className={`v2-module-square-button-group${(!tileImageOptionAvailable && !coverImageOptionAvailable && !containImageOptionAvailable) ? ' v2-module-disabled-field' : ''}`}>
                                <div className={`v2-module-square-button-holder`}>
                                    <div className={`v2-module-square-button${(tileImageOptionAvailable && tileImage.toString() === tileImageValueLink.toLowerCase()) ? ' v2-module-button__active v2-no-mouse-events' : ''}${!tileImageOptionAvailable ? ' v2-module-disabled-field' : ''}`}
                                        onClick={changeImageFillTypeTile}
                                    >
                                        <div className={`v2-module-square-button__icon v2-module-filltype-image-tiled`} />
                                    </div>
                                </div>
                                <div className={`v2-module-square-button-holder`}>
                                    <div className={`v2-module-square-button${(coverImageOptionAvailable && coverImage.toString() === coverImageValueLink.toLowerCase()) ? ' v2-module-button__active v2-no-mouse-events' : ''}${!coverImageOptionAvailable ? ' v2-module-disabled-field' : ''}`}
                                        onClick={changeImageFillTypeCover}
                                    >
                                        <div className={`v2-module-square-button__icon v2-module-filltype-image-cover`} />
                                    </div>
                                </div>
                                <div className={`v2-module-square-button-holder`}>
                                    <div className={`v2-module-square-button${(containImageOptionAvailable && containImage.toString() === containImageValueLink.toLowerCase()) ? ' v2-module-button__active v2-no-mouse-events' : ''}${!containImageOptionAvailable ? ' v2-module-disabled-field' : ''}`}
                                        onClick={changeImageFillTypeContain}
                                    >
                                        <div className={`v2-module-square-button__icon v2-module-filltype-image-contain`} />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className='v2-module-one-column'>
                            <div className='v2-module-field-label'>Image</div>
                            <ModuleTextInput
                                fieldLabel={<div className="v2-module-block-field-input-label" style={{backgroundColor: '#FFFFFF', backgroundImage: `url(${ImageIcon})`, backgroundSize: '1.8rem', backgroundRepeat: 'no-repeat', backgroundPosition: 'center'}} />}
                                value={typeof imageFileData === 'string' ? imageFileData : imageFileData?.originalFile ?? '<No Image>'}
                                fieldId={'fill-image'}
                                enabled={true}
                                editable={false}
                                onChange={null}
                                onClick={
                                    () => {
                                        console.log('Image field clicked, current imageFileData:', imageFileData, groupConfig, moduleConfig);
                                        // Trigger image uploader slide out panel
                                        slideOutPanelControl.openImageUploader(
                                            imageFileData,
                                            (img) => changeImageValue(img, imageFillTypeConfig?.options?.['customisation-module-fill-type-image-options-image'], false),  // Preview callback (when file selected)
                                            (img) => changeImageValue(img, imageFillTypeConfig?.options?.['customisation-module-fill-type-image-options-image'], true),   // Confirm callback (when uploaded to server)
                                            'Upload Image',
                                            imageFillTypeConfig
                                        );
                                        /* props.openTemporaryImageUploadPopup(
                                            imageFileData,
                                            (groupConfig?.parent?.name ? groupConfig?.parent?.name + ' > ' : '') + (groupConfig?.name ? groupConfig?.name + ' > ' : '') + moduleConfig.name,
                                            'This is a temporary image uploader for the Fill Module and is not final.',
                                        ) */
                                    }
                                }
                            />
                        </div>
                    </div>
                </>
            }

            {renderSecondCustomBlock(props)}
        </div>
    );
}

export default FillModule;
